import { useEffect, useState } from 'react';
import type { ImageNaturalSize } from './types';
import { createLogger } from '@/shared/lib/logger';

const logger = createLogger('useImageLoader');

interface UseImageLoaderProps {
  imageUrl: string;
}

interface UseImageLoaderReturn {
  imageLoaded: boolean;
  imageError: string | null;
  imageNaturalSize: ImageNaturalSize;
}

const EMPTY_SIZE: ImageNaturalSize = { width: 0, height: 0 };

interface LoadResult {
  /** The URL this result belongs to. */
  url: string;
  naturalSize: ImageNaturalSize;
  error: string | null;
}

/**
 * Loads `imageUrl` and reports its natural size. The result is keyed by the
 * URL it was produced for, so the instant the URL changes (next image of a
 * batch, a rotation) the hook reports "not loaded" again. It used to keep
 * `imageLoaded` and the previous image's size across the switch, so the
 * cropper sized the new image's frame with the old one's dimensions and its
 * Confirm button cut the new image with the previous image's frame.
 */
export function useImageLoader({
  imageUrl,
}: UseImageLoaderProps): UseImageLoaderReturn {
  const [result, setResult] = useState<LoadResult | null>(null);

  useEffect(() => {
    if (!imageUrl) {
      console.error('[useImageLoader] No image URL provided');
      return;
    }

    logger.info('Loading image from URL:', imageUrl.substring(0, 50) + '...');

    const img = new Image();
    const loadStartTime = performance.now();

    img.onload = () => {
      const loadTime = performance.now() - loadStartTime;

      // Use naturalWidth/Height (not width/height) so the size matches the
      // EXIF-oriented dimensions the browser also applies to the displayed
      // <img>. Mixing the two transposes the scale for rotated phone photos
      // and drifts the crop frame off-screen.
      setResult({
        url: imageUrl,
        naturalSize: { width: img.naturalWidth, height: img.naturalHeight },
        error: null,
      });

      if (loadTime > 3000) {
        console.warn(
          '[useImageLoader] Image loading took longer than 3s - possible mobile performance issue'
        );
      }
    };

    img.onerror = (e) => {
      const loadTime = performance.now() - loadStartTime;

      console.error('[useImageLoader] Failed to load image:', {
        error: e,
        imageUrl: imageUrl.substring(0, 100),
        loadTimeMs: loadTime.toFixed(0),
        errorType:
          typeof e === 'object' && e && 'type' in e ? e.type : 'unknown',
      });

      setResult({
        url: imageUrl,
        naturalSize: EMPTY_SIZE,
        error:
          'Failed to load image. The file may be corrupted or your device may be out of memory.',
      });
    };

    img.src = imageUrl;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [imageUrl]);

  if (!imageUrl) {
    return {
      imageLoaded: false,
      imageError: 'No image URL provided',
      imageNaturalSize: EMPTY_SIZE,
    };
  }

  const current = result?.url === imageUrl ? result : null;
  return {
    imageLoaded: current !== null && current.error === null,
    imageError: current?.error ?? null,
    imageNaturalSize: current?.naturalSize ?? EMPTY_SIZE,
  };
}
